import { Button } from '@ipfabric/ui-components/Button/Button';
import type { Filters } from '@ipfabric/utils';
import { nullable } from '@ipfabric/utils/fp/nullable';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { useApiTableAdvancedFilterColumns } from 'components/ApiTable/components/useApiTableAdvancedFilterColumns';
import { isFiltersSimple } from 'components/ApiTable/helpers/isFiltersSimple';
import { useApiTableContextSelectors } from 'components/ApiTable/model/ApiTableModel';
import { AdvancedFilter } from 'components/Table/components/AdvancedFilter/AdvancedFilter';
import { getFromFilters } from 'components/Table/components/AdvancedFilter/getFromFilters';
import {
  type GroupFilter,
  useAdvancedFilter,
} from 'components/Table/components/AdvancedFilter/useAdvancedFilter';
import { useRuleContext } from 'components/Table/components/AdvancedFilter/useRuleContext';
import { loadTableSettings } from 'components/Table/model/commands/loadTableSettings';
import { useLocale } from 'containers/App/LocaleContext';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import { predicate } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useEffect, useState } from 'react';
import type { FC } from 'react';
import { useConfirmDialog } from 'utils/hooks/useConfirmDialog';
import { useEffectOnChange } from 'utils/react/useEffectOnChange';
import { selectColumnsByKey } from '../../Table/model/selectors/selectColumnsByKey';
import { changeAdvancedFilter } from '../model/commands/changeAdvancedFilter';
import { closeAdvancedFilter } from '../model/commands/closeAdvancedFilter';
import { deleteFilterPreset } from '../model/commands/deleteFilterPreset';
import { submitAdvancedFilters } from '../model/commands/submitAdvancedFilters';
import { updateFilterPreset } from '../model/commands/updateFilterPreset';
import { ApiTableSaveFilterModal } from './ApiTableSaveFilterModal';

const CONFIRM_DELETE_DIALOG = 'apiTableFiltersConfirmDeleteDialog';
const CONFIRM_UPDATE_DIALOG = 'apiTableFiltersConfirmUpdateDialog';

type Props = {
  className?: string;
};

export const ApiTableAdvancedFilter: FC<Props> = (props) => {
  const {
    apiTable: { advancedFilter: advancedFilterMsg },
  } = useLocale();
  const [submitCount, setSubmitCount] = useState<number>(0);
  const [save, setSave] = useState<boolean>(false);
  const ctxProps = useApiTableContextSelectors({
    advancedFilter: prop('advancedFilter'),
    columnsByKey: (s) => selectColumnsByKey(s.tableModel),
    dispatch: prop('dispatch'),
    initialFilters: prop('initialFilters'),
    selectedFilterPreset: prop('selectedFilterPreset'),
    tableDispatch: prop('tableModel', 'dispatch'),
  });
  const columns = useApiTableAdvancedFilterColumns();
  const confirmDeleteDialog = useConfirmDialog({ name: CONFIRM_DELETE_DIALOG });
  const confirmUpdateDialog = useConfirmDialog({ name: CONFIRM_UPDATE_DIALOG });

  const ruleContext = useRuleContext();

  const advancedFilterHook = useAdvancedFilter({
    initialValue: (): GroupFilter =>
      getFromFilters(
        ctxProps.selectedFilterPreset?.filter ??
          pipe(
            ctxProps.initialFilters ?? null,
            nullable.chainPredicate(
              predicate.not(isFiltersSimple(ctxProps.columnsByKey)),
            ),
          ) ??
          {},
        columns,
        ruleContext,
      ),
  });

  const handleFilterChange = (filters: Filters | undefined) => {
    ctxProps.dispatch(changeAdvancedFilter, { filters });
  };

  const handleSubmit = () => {
    setSubmitCount(submitCount + 1);
    ctxProps.dispatch(submitAdvancedFilters);
  };

  const loadSettings = () => {
    ctxProps.tableDispatch(loadTableSettings);
  };

  const handleDelete = async () => {
    if (ctxProps.selectedFilterPreset) {
      await ctxProps.dispatch(deleteFilterPreset, {
        preset: ctxProps.selectedFilterPreset,
      });

      loadSettings();
    }
  };

  const handleUpdate = async () => {
    if (ctxProps.selectedFilterPreset) {
      await ctxProps.dispatch(updateFilterPreset, {
        preset: ctxProps.selectedFilterPreset,
      });

      loadSettings();
    }
  };

  useEffectOnChange(() => {
    advancedFilterHook.handleFilterChange(
      [],
      getFromFilters(
        ctxProps.selectedFilterPreset?.filter ?? {},
        columns,
        ruleContext,
      ),
    );
  }, [ctxProps.selectedFilterPreset?.filter]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: retrigger only when external filters change
  useEffect(() => {
    if (ctxProps.advancedFilter.externalFilters !== undefined) {
      advancedFilterHook.handleFilterChange(
        [],
        getFromFilters(
          ctxProps.advancedFilter.externalFilters,
          columns,
          ruleContext,
        ),
      );
    }
  }, [ctxProps.advancedFilter.externalFilters]);

  if (!ctxProps.advancedFilter.open) {
    return null;
  }

  const validToSave =
    ctxProps.advancedFilter.filters &&
    Object.keys(ctxProps.advancedFilter.filters).length > 0;

  return (
    <>
      <ConfirmDialog
        id={CONFIRM_DELETE_DIALOG}
        msg={advancedFilterMsg.deleteDialog}
        onClickConfirm={handleDelete}
        onClickSecondaryConfirm={() => {
          confirmDeleteDialog.closeConfirmDialog();
        }}
        btnConfirmVariant='primary'
        btnConfirmAccent='negative'
        btnCancelVariant='secondary'
        btnCancelAccent='positive'
      />
      <ConfirmDialog
        id={CONFIRM_UPDATE_DIALOG}
        msg={{
          header: advancedFilterMsg.updateDialog.header,
          subHeader: advancedFilterMsg.updateDialog.subHeader(
            ctxProps.selectedFilterPreset?.name ?? '',
          ),
          btnConfirm: advancedFilterMsg.updateDialog.btnConfirm,
        }}
        onClickConfirm={handleUpdate}
      />
      {save && (
        <ApiTableSaveFilterModal
          onClose={() => {
            setSave(false);
          }}
          loadTableSettings={loadSettings}
        />
      )}
      <div
        className={classNames(
          'relative bg-colored-light border-t border-b border-divider border-solid p-sm',
          props.className,
        )}
      >
        <AdvancedFilter
          advancedFilterHook={advancedFilterHook}
          columns={columns}
          onFilterChange={handleFilterChange}
          submitCount={submitCount}
          onSubmit={handleSubmit}
        />
        <div className='mt flex flex-row gap-xs'>
          <Button
            type='button'
            variant='primary'
            onClick={() => {
              handleSubmit();
            }}
          >
            Apply filter
          </Button>
          {ctxProps.selectedFilterPreset !== null && (
            <Button
              type='button'
              variant='outline'
              onClick={() => {
                confirmUpdateDialog.toggleConfirmDialog();
              }}
              disabled={!validToSave}
            >
              {advancedFilterMsg.saveBtn}
            </Button>
          )}
          <Button
            type='button'
            variant='outline'
            onClick={() => {
              setSave(true);
            }}
            disabled={!validToSave}
          >
            {advancedFilterMsg.saveAsBtn}
          </Button>
          {ctxProps.selectedFilterPreset !== null && (
            <Button
              type='button'
              variant='outline'
              accent='negative'
              onClick={() => {
                confirmDeleteDialog.toggleConfirmDialog();
              }}
            >
              {advancedFilterMsg.deleteBtn}
            </Button>
          )}
          <Button
            type='button'
            variant='plain'
            onClick={() => {
              ctxProps.dispatch(closeAdvancedFilter);
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </>
  );
};
